/**
 * The top toolbar. Mirrors the prototype's layout: title on the left
 * (editable), action buttons on the right, scene + issue counts in the
 * middle when there's space.
 *
 * Phase 1b.3 wires: title edit, import, export, Variables button (with
 * count chip), Objectives button (with count chip), clickable Problems
 * button (opens the Problems modal), and the panel-collapse toggle.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  Download,
  FolderOpen,
  History,
  PanelRightClose,
  PanelRightOpen,
  Package,
  Play,
  Plus,
  Settings,
  SlidersHorizontal,
  Target,
  Upload,
  UploadCloud,
  Variable,
} from 'lucide-react';
import { useScenarioStore } from '../store/scenarioStore';
import { launchPreview } from '../preview/launchPreview';
import { downloadScormPackage } from '../scorm/buildScormPackage';

export function Toolbar() {
  const scenario = useScenarioStore((s) => s.scenario);
  const setScenario = useScenarioStore((s) => s.setScenario);
  const setTitle = useScenarioStore((s) => s.setTitle);
  const getValidation = useScenarioStore((s) => s.getValidation);
  const panelOpen = useScenarioStore((s) => s.panelOpen);
  const setPanelOpen = useScenarioStore((s) => s.setPanelOpen);
  const setOpenModal = useScenarioStore((s) => s.setOpenModal);
  const addNode = useScenarioStore((s) => s.addNode);
  const publishDraft = useScenarioStore((s) => s.publishDraft);
  const publishedVersions = useScenarioStore((s) => s.publishedVersions);
  const saveStatus = useScenarioStore((s) => s.saveStatus);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const validation = getValidation();
  const errorCount = validation.errors.length;
  const warningCount = validation.warnings.length;
  const totalIssues = errorCount + warningCount;
  const variableCount = Object.keys(scenario.variables || {}).length;
  const objectiveCount = Object.keys(scenario.objectives || {}).length;
  const versionCount = publishedVersions.length;

  const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const exportScorm = async () => {
    const result = await downloadScormPackage(scenario);
    if (!result.ok) {
      window.alert(
        'This scenario has validation errors, so it can\u2019t be exported to SCORM yet. ' +
          'Open Problems to fix them:\n\n' +
          result.errors.slice(0, 10).join('\n') +
          (result.errors.length > 10 ? `\n\u2026and ${result.errors.length - 10} more.` : '')
      );
    }
  };

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(scenario, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${scenario.title.replace(/\s+/g, '_').toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importJSON = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target?.result as string);
        if (parsed && parsed.nodes && parsed.startNodeId) {
          setScenario(parsed); // migrateIfNeeded runs in the store
        } else {
          alert('That file does not look like a valid scenario.');
        }
      } catch {
        alert('That file does not look like a valid scenario.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const onPublish = () => {
    const suggested = `v${versionCount + 1}`;
    const tag = prompt(
      `Publish the current draft as a new version.\n\nVersion tag (e.g., "${suggested}", "launch", "2024-12-pilot"):`,
      suggested,
    );
    if (tag === null) return; // user cancelled
    const result = publishDraft(tag);
    if (!result.ok) {
      alert(`Couldn't publish: ${result.error}`);
      return;
    }
    // Success — surface the new version by opening the Versions modal.
    setOpenModal('versions');
  };

  return (
    <header className="flex items-center justify-between px-6 py-3 border-b border-cream-border bg-cream">
      <div className="flex items-baseline gap-3">
        <h1 className="text-lg font-bold text-ink">Branchworks</h1>
        <span className="text-xs uppercase tracking-widest text-ink-2">MVP</span>
        <span className="ml-4 text-sm text-ink-2">
          Editing:{' '}
          <input
            value={scenario.title}
            onChange={onTitleChange}
            className="bg-transparent border-b border-dotted border-ink-3 text-ink px-1 focus:outline-none focus:border-solid"
            style={{ minWidth: 200 }}
          />
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-ink-3 mr-1">
          {Object.keys(scenario.nodes).length} scenes
        </span>
        <SaveStatusIndicator status={saveStatus} />
        <div className="w-px h-5 mx-1 bg-cream-border" />
        <button
          onClick={() => setOpenModal('library')}
          className="px-3 py-1.5 text-sm border border-cream-border rounded flex items-center gap-1.5 hover:bg-cream-2"
          title="Open the scenario library"
        >
          <FolderOpen size={14} /> My scenarios
        </button>
        <button
          onClick={() => addNode()}
          className="px-3 py-1.5 text-sm border border-cream-border rounded flex items-center gap-1.5 hover:bg-cream-2"
          title="Add a new scene to the canvas"
        >
          <Plus size={14} /> Add scene
        </button>
        <div className="w-px h-5 mx-1 bg-cream-border" />
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json"
          onChange={importJSON}
          className="hidden"
        />
        <Menu label="Export" icon={<Download size={14} />}>
          <MenuItem
            icon={<Upload size={14} />}
            label="Import JSON…"
            onClick={() => fileInputRef.current?.click()}
          />
          <MenuItem icon={<Download size={14} />} label="Export JSON" onClick={exportJSON} />
          <MenuItem
            icon={<Package size={14} />}
            label="Export SCORM"
            title="SCORM 1.2 package (completion-only) for an LMS"
            onClick={exportScorm}
          />
        </Menu>
        <Menu label="Tools" icon={<SlidersHorizontal size={14} />}>
          <MenuItem
            icon={<Variable size={14} />}
            label="Variables"
            badge={variableCount}
            onClick={() => setOpenModal('variables')}
          />
          <MenuItem
            icon={<Target size={14} />}
            label="Objectives"
            badge={objectiveCount}
            onClick={() => setOpenModal('objectives')}
          />
          <MenuItem
            icon={<Settings size={14} />}
            label="Settings"
            title="Title screen, end screen, and branding"
            onClick={() => setOpenModal('settings')}
          />
          <MenuItem
            icon={<History size={14} />}
            label="Versions"
            badge={versionCount}
            onClick={() => setOpenModal('versions')}
          />
        </Menu>
        <button
          onClick={onPublish}
          className="px-3 py-1.5 text-sm border border-cream-border rounded flex items-center gap-1.5 hover:bg-cream-2"
          title="Snapshot the current draft as a published version. Errors block publish."
        >
          <UploadCloud size={14} /> Publish
        </button>
        {totalIssues > 0 && (
          <button
            onClick={() => setOpenModal('problems')}
            className="px-3 py-1.5 text-sm border rounded flex items-center gap-1.5 hover:bg-cream-2"
            style={{
              borderColor: errorCount > 0 ? '#A14A38' : '#B8551F',
              color: errorCount > 0 ? '#A14A38' : '#B8551F',
            }}
            title={`${errorCount} error${errorCount === 1 ? '' : 's'}, ${warningCount} warning${warningCount === 1 ? '' : 's'} — click to view`}
          >
            {errorCount > 0 ? <AlertCircle size={14} /> : <AlertTriangle size={14} />}
            Problems
            <span
              className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white"
              style={{ background: errorCount > 0 ? '#A14A38' : '#B8551F' }}
            >
              {totalIssues}
            </span>
          </button>
        )}
        <button
          onClick={() => launchPreview(scenario)}
          className="px-4 py-1.5 text-sm rounded text-white flex items-center gap-1.5"
          style={{ background: '#B8551F' }}
          title="Open the runtime in a new tab against the current draft"
        >
          <Play size={14} /> Preview
        </button>
        <div className="w-px h-5 mx-1 bg-cream-border" />
        <button
          onClick={() => setPanelOpen(!panelOpen)}
          className="p-1.5 border border-cream-border rounded flex items-center text-ink-2 hover:bg-cream-2"
          title={panelOpen ? 'Hide details panel' : 'Show details panel'}
          aria-label={panelOpen ? 'Hide details panel' : 'Show details panel'}
        >
          {panelOpen ? <PanelRightClose size={16} /> : <PanelRightOpen size={16} />}
        </button>
      </div>
    </header>
  );
}

/**
 * Small autosave status indicator. Mirrors the pattern editors like Google
 * Docs use: a quiet text cue rather than a loud button. "Saving…" while a
 * write is in flight, "Saved" once it lands, an error tint if it failed.
 */
function SaveStatusIndicator({ status }: { status: 'idle' | 'saving' | 'saved' | 'error' }) {
  if (status === 'idle') return null;
  const label =
    status === 'saving' ? 'Saving…' : status === 'saved' ? 'Saved' : 'Save failed';
  const color = status === 'error' ? '#A14A38' : '#999388';
  return (
    <span className="text-xs mr-1" style={{ color }} title="Autosave to local storage">
      {label}
    </span>
  );
}

/**
 * A small toolbar dropdown. Groups secondary actions so the header stays
 * compact. Closes on outside click, Escape, or selecting an item (the click
 * bubbles to the panel, which closes it).
 */
function Menu({ label, icon, children }: { label: string; icon: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="px-3 py-1.5 text-sm border border-cream-border rounded flex items-center gap-1.5 hover:bg-cream-2"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {icon} {label}
        <ChevronDown size={12} className="opacity-50" />
      </button>
      {open && (
        <div
          role="menu"
          onClick={() => setOpen(false)}
          className="absolute top-full right-0 mt-1 min-w-[210px] rounded border border-cream-border bg-white py-1 z-50"
          style={{ boxShadow: '0 6px 24px rgba(0,0,0,0.12)' }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  badge,
  title,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  badge?: number;
  title?: string;
}) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      title={title}
      className="w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 text-ink hover:bg-cream-2"
    >
      <span className="text-ink-2">{icon}</span>
      <span className="flex-1">{label}</span>
      {badge ? (
        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-ink text-cream-2">
          {badge}
        </span>
      ) : null}
    </button>
  );
}
